import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './BuildOrderView.css'; // Reuse the same CSS styles
import { getStoredToken } from '../authUtils';

const ParsedReplayView = ({ replay, apiBaseUrl }) => {
  const [parsedReplay, setParsedReplay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState('');

  useEffect(() => {
    fetchParsedReplay();
    fetchAIAnalysis();
    checkAnalysisStatus();
  }, [replay.replay_id]);

  // Periodically check analysis status - only when analysis is in progress
  useEffect(() => {
    if (!aiLoading) {
      // Don't start interval if analysis is not running
      return;
    }

    const interval = setInterval(() => {
      checkAnalysisStatus();
    }, 2000); // Check every 2 seconds

    return () => clearInterval(interval);
  }, [replay.replay_id, aiLoading]);

  const authHeaders = () => {
    const token = getStoredToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const fetchAIAnalysis = async () => {
    console.log(`[AI Analysis] Fetching existing analysis for replay ${replay.replay_id}`);
    try {
      setAiError(null);
      const response = await fetch(`${apiBaseUrl}/ai_analysis/${replay.replay_id}`, { headers: authHeaders() });
      console.log(`[AI Analysis] Fetch response status: ${response.status}`);
      
      if (!response.ok) {
        console.log('[AI Analysis] No existing analysis found');
        setAiAnalysis(null);
        return;
      }
      const data = await response.json();
      console.log('[AI Analysis] Existing analysis loaded:', data);
      setAiAnalysis(data);
    } catch (e) {
      console.error('[AI Analysis] Fetch failed:', e);
      setAiError(e.message);
    }
  };

  const checkAnalysisStatus = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/ai_analysis/status/${replay.replay_id}`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        console.log(`[AI Analysis] Status check: analyzing=${data.is_analyzing}`);
        setAiLoading(data.is_analyzing);
        
        // If analysis was running but now stopped, refresh the analysis
        if (aiLoading && !data.is_analyzing) {
          console.log('[AI Analysis] Analysis completed, fetching result');
          await fetchAIAnalysis();
        }
      }
    } catch (e) {
      console.error('[AI Analysis] Status check failed:', e);
    }
  };

  const triggerAIAnalysis = async (force = false) => {
    console.log(`[AI Analysis] Starting analysis for replay ${replay.replay_id}, force=${force}`);
    try {
      setAiError(null);
      const url = `${apiBaseUrl}/ai_analysis/trigger/${replay.replay_id}${force ? '?force=true' : ''}`;
      console.log(`[AI Analysis] Sending POST request to: ${url}`);
      
      const response = await fetch(url, { method: 'POST', headers: authHeaders() });
      console.log(`[AI Analysis] Trigger response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AI Analysis] Trigger failed: ${response.status} - ${errorText}`);
        throw new Error('Failed to trigger AI analysis');
      }
      
      const triggerResult = await response.json();
      console.log('[AI Analysis] Trigger successful:', triggerResult);
      
      // Set loading state and let the periodic status check handle the rest
      setAiLoading(true);
      console.log('[AI Analysis] Analysis triggered, status will be checked automatically');
      
    } catch (e) {
      console.error('[AI Analysis] Trigger failed:', e);
      setAiError(e.message);
      setAiLoading(false);
    }
  };

  const fetchParsedReplay = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = authHeaders();
      
      // First check if the replay is already parsed
      const response = await fetch(`${apiBaseUrl}/parsed_replay/${replay.replay_id}`, {
        headers
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // Replay not parsed yet, trigger parsing
          await triggerParsing();
          return;
        }
        throw new Error('Failed to fetch parsed replay');
      }
      
      const data = await response.json();
      setParsedReplay(data);
      
      // Auto-select first player if available
      if (data.build_orders && data.build_orders.length > 0) {
        setSelectedPlayer(data.build_orders[0].player);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const triggerParsing = async (force = false) => {
    try {
      setError(null);
      const headers = authHeaders();
      
      const url = `${apiBaseUrl}/parsed_replay/trigger/${replay.replay_id}${force ? '?force=true' : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        throw new Error('Failed to trigger parsing');
      }
      
      await response.json();
      setParsedReplay(null);
      setError(`Parsing triggered${force ? ' (forced)' : ''}. Please wait and refresh manually...`);
    } catch (err) {
      setError(`Failed to trigger parsing: ${err.message}`);
    }
  };

  const triggerForceReparse = async () => {
    console.log(`[Force Re-parse] Starting force re-parse for replay ${replay.replay_id}`);
    try {
      setError(null);
      const headers = authHeaders();
      
      const url = `${apiBaseUrl}/parsed_replay/force_reparse/${replay.replay_id}`;
      console.log(`[Force Re-parse] Sending POST request to: ${url}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers
      });
      
      console.log(`[Force Re-parse] Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Force Re-parse] Failed: ${response.status} - ${errorText}`);
        throw new Error('Failed to trigger force re-parse');
      }
      
      const result = await response.json();
      console.log('[Force Re-parse] Success:', result);
      
      // Clear current parsed replay and show message
      setParsedReplay(null);
      setError('Force re-parse triggered. The existing parsed data has been deleted and new parsing started. Please wait and refresh manually...');
      
    } catch (err) {
      console.error('[Force Re-parse] Error:', err);
      setError(`Failed to trigger force re-parse: ${err.message}`);
    }
  };

  const formatTime = (seconds) => {
    if (typeof seconds === 'string') {
      // Handle time string format
      const date = new Date(seconds);
      return date.toLocaleTimeString();
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getSelectedPlayerData = () => {
    if (!parsedReplay || !selectedPlayer) return null;
    return parsedReplay.build_orders.find(bo => bo.player === selectedPlayer);
  };

  const playerData = getSelectedPlayerData();

  return (
    <div className="build-order-view">
      <div className="replay-header">
        <h2>{replay.map_name}</h2>
        <div className="replay-meta">
          <span>Replay #{replay.replay_id}</span>
          <span>Duration: {formatTime(replay.game_length)}</span>
          <span>Date: {formatDate(replay.replay_date)}</span>
          <span>Winner: {replay.winning_player}</span>
        </div>
      </div>

      {loading && <div className="loading">Loading parsed replay...</div>}
      
      {error && (
        <div className="error">
          <p>Error: {error}</p>
          <div>
            <button onClick={fetchParsedReplay} className="retry-btn">
              Retry
            </button>
            <button onClick={() => triggerParsing(false)} className="retry-btn" style={{marginLeft: '10px'}}>
              Trigger Parsing (Today Only)
            </button>
            <button onClick={() => triggerParsing(true)} className="retry-btn" style={{marginLeft: '10px'}}>
              Trigger Parsing (Force)
            </button>
          </div>
          <div style={{marginTop: '10px', fontSize: '0.9em', color: '#666'}}>
            <p><strong>Today Only:</strong> Only parses replays from today</p>
            <p><strong>Force:</strong> Parses any replay regardless of date</p>
          </div>
        </div>
      )}

      {parsedReplay && (
        <>
          {/* Game Info Section */}
          {parsedReplay.info && (
            <div className="game-info">
              <h3>Game Information</h3>
              <div className="info-grid">
                <div>Version: {parsedReplay.info.version}</div>
                <div>Game Type: {parsedReplay.info.game_type}</div>
                <div>Map: {parsedReplay.info.map_name}</div>
                <div>Players: {parsedReplay.info.players?.map(p => p.name).join(', ')}</div>
                {parsedReplay.info.duration_stats && (
                  <div>
                    Duration: {parsedReplay.info.duration_stats.game_length_mins}:
                    {parsedReplay.info.duration_stats.game_length_seconds?.toString().padStart(2, '0')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Players Details */}
          {parsedReplay.info && parsedReplay.info.players && (
            <div className="players-info">
              <h3>Players</h3>
              <div className="players-grid">
                {parsedReplay.info.players.map((player, index) => (
                  <div key={index} className="player-card">
                    <div className="player-name">{player.name}</div>
                    <div className="player-details">
                      <span className="player-race">{player.race}</span>
                      <span className="player-team">Team {player.team_id}</span>
                      <span className={`player-result ${player.result.toLowerCase()}`}>
                        {player.result}
                      </span>
                      <span className="player-apm">APM: {player.apm?.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {parsedReplay.info.winner && (
                <div className="winner-info">
                  <strong>Winner: {parsedReplay.info.winner}</strong>
                  {parsedReplay.info.winning_team && (
                    <span> (Team {parsedReplay.info.winning_team})</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Player Selection */}
          {parsedReplay.build_orders && parsedReplay.build_orders.length > 0 && (
            <div className="player-selection">
              <h3>Select Player:</h3>
              <div className="player-buttons">
                {parsedReplay.build_orders.map((buildOrder, index) => (
                  <button
                    key={buildOrder.player}
                    className={`player-btn ${selectedPlayer === buildOrder.player ? 'active' : ''}`}
                    onClick={() => setSelectedPlayer(buildOrder.player)}
                  >
                    {buildOrder.player}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Build Order for Selected Player */}
          {selectedPlayer && playerData && (
            <div className="build-order-section">
              <h3>Events for {selectedPlayer}</h3>
              
              {playerData.game_events && playerData.game_events.length > 0 ? (
                <div className="build-order-content">
                  <div className="build-order-list">
                    <div className="build-order-header">
                      <span>Time</span>
                      <span>Supply</span>
                      <span>Event</span>
                    </div>
                    
                    {playerData.game_events.map((event, index) => (
                      <div key={index} className="build-order-item">
                        <span className="time">{formatTime(event.start_real_time)}</span>
                        <span className="supply">{event.supply}</span>
                        <span className="name">{event.name}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="build-order-stats">
                    Total events: {playerData.game_events.length}
                  </div>
                </div>
              ) : (
                <div className="no-data">No game events available for this player</div>
              )}
            </div>
          )}

          {/* AI Analysis Section */}
          <div className="build-order-section">
            <h3>AI Analysis</h3>
            
            {/* Analysis Status Indicator */}
            {aiLoading && (
              <div className="analysis-status" style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div className="spinner" style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #2196f3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div>
                  <strong>🤖 AI Analysis in Progress...</strong>
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                    Analyzing replay data and generating insights. This may take a few moments.
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
              <button 
                className="retry-btn" 
                onClick={() => triggerAIAnalysis(true)} 
                disabled={aiLoading}
                style={{
                  opacity: aiLoading ? 0.6 : 1,
                  cursor: aiLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {aiLoading ? '🔄 Analyzing...' : '🚀 Force Re-analyze'}
              </button>
              <button 
                className="retry-btn" 
                onClick={triggerForceReparse}
                style={{
                  backgroundColor: '#ff6b35',
                  borderColor: '#ff6b35'
                }}
              >
                🔄 Force Re-parse
              </button>
            </div>
            {aiError && <div className="error">{aiError}</div>}
            {aiAnalysis ? (
              <div className="build-order-content ai-analysis-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiAnalysis.markdown}</ReactMarkdown>
              </div>
            ) : !aiLoading && (
              <div className="no-data">No AI analysis yet</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ParsedReplayView;
